/**
 * The CrazyGames HTML5 SDK (v3), loaded only inside the CrazyGames frame (lib/portalEmbed.ts).
 * Suikamon uses its user module for Login with CrazyGames: the one sign-in CrazyGames allows
 * (docs.crazygames.com/sdk/html5-v3/user). Everywhere else the script is never requested.
 *
 * The SDK answers in three environments: `crazygames` (their site), `local` (localhost, with
 * mocked users) and `disabled` (anywhere else, where every call throws). Only the first two
 * offer accounts.
 */

export const CRAZYGAMES_SDK_URL = "https://sdk.crazygames.com/crazygames-sdk-v3.js";

export interface CrazyGamesUser {
  username: string;
  profilePictureUrl?: string;
}

interface CrazyGamesUserModule {
  isUserAccountAvailable: boolean;
  getUser: () => Promise<CrazyGamesUser | null>;
  getUserToken: () => Promise<string>;
  showAuthPrompt: () => Promise<CrazyGamesUser | null>;
  addAuthListener: (listener: (user: CrazyGamesUser | null) => void) => void;
}

interface CrazyGamesSdk {
  init: () => Promise<void>;
  environment: "crazygames" | "local" | "disabled";
  user: CrazyGamesUserModule;
}

declare global {
  interface Window {
    CrazyGames?: { SDK: CrazyGamesSdk };
  }
}

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = CRAZYGAMES_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("The CrazyGames SDK did not load"));
    document.head.appendChild(script);
  });
}

let ready: Promise<CrazyGamesUserModule | null> | null = null;

/**
 * The SDK's user module once it is loaded and initialised, or null when accounts are not
 * available (the SDK failed to load, the environment is `disabled`, or CrazyGames reports
 * accounts unavailable on this embed). Loads the script at most once.
 */
export function crazyGamesUser(): Promise<CrazyGamesUserModule | null> {
  if (!ready) {
    ready = (async () => {
      try {
        if (!window.CrazyGames) await loadScript();
        const sdk = window.CrazyGames?.SDK;
        if (!sdk) return null;
        await sdk.init();
        if (sdk.environment === "disabled" || !sdk.user.isUserAccountAvailable) return null;
        return sdk.user;
      } catch (error) {
        console.warn("[crazygames] SDK unavailable", error);
        return null;
      }
    })();
  }
  return ready;
}

/** Test seam: forget the loaded SDK. */
export function resetCrazyGamesSdkForTests(): void {
  ready = null;
}
